import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { DateRangePicker, PickerHandle } from "rsuite";
import { DateRange, RangeType } from "rsuite/esm/DateRangePicker";

import crossIconStyle from "./all-workflows-table.scss";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { StoreState } from "store";
import { applyMode } from "store/theme";
import formStyle from "styles/form.scss";
import { resolveColor } from "utils/commonFunctions";
import { formatDateWithoutTime, toUtcEndOfDay, toUtcStartOfDay } from "utils/format";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    from: (date: string) => void;
    to: (date: string) => void;
}

const mapState = (state: StoreState) => ({
    themeName: state.themeReducer.themeName,
    theme: state.themeReducer.theme,
});

const connector = connect(mapState, { applyMode });

const SidePanelDatePicker = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const dateRangePicker = React.useRef<PickerHandle>(null);
    const [dateRange, setDateRange] = React.useState({
        from: "",
        to: "",
    });
    const { t } = useTranslation();
    const selectableRanges: RangeType[] = [];

    React.useEffect(() => {
        let dateRangeInput: Element | null | undefined;
        try {
            dateRangeInput = dateRangePicker.current?.target
                ?.getElementsByClassName("rs-picker-toggle-textbox rs-picker-toggle-read-only")
                .item(0);
            if (dateRangeInput != null) {
                dateRangeInput.setAttribute("data-testid", testIds.workArea.license.licenseEvents.dateRangePickerInput);
            }

            const closeButton = document.getElementsByClassName(
                "rs-picker-toggle-clean rs-btn-close"
            )[0] as HTMLElement;
            if (closeButton) {
                closeButton.setAttribute("class", crossIconStyle.dragCross);
                closeButton.setAttribute("alt", t("AltText.clearIcon"));
            }
        } catch (error) {
            logger.error(
                "Failed to find the input element when trying to set its test ID for date range picker.",
                error
            );
        }
    });

    return (
        <>
            <DateRangePicker
                ref={dateRangePicker}
                caretAs={(colorProps) => (
                    <CalendarIcon {...colorProps} color={resolveColor("--theme-icon-fill-default")} />
                )}
                className={classNames(formStyle.dateRange)}
                editable={false}
                ranges={selectableRanges}
                placement={"bottomEnd"}
                onOk={(date: DateRange) => {
                    const start = toUtcStartOfDay(date[0]);
                    const end = toUtcEndOfDay(date[1]);
                    props.from(start);
                    props.to(end);
                    setDateRange({
                        from: start,
                        to: end,
                    });
                    // Only track the event, don't fetch data yet
                    usageStatisticsService.sendEvent({
                        category: Category.WORKFLOW,
                        action: Action.FILTER_BY_DATE,
                    });
                }}
                renderValue={(value: DateRange) => {
                    return `${formatDateWithoutTime(value[0].toString())} - ${formatDateWithoutTime(
                        value[1].toString()
                    )}`;
                }}
                placeholder={
                    dateRange.to == ""
                        ? t("WorkflowsTable.filter.selectDate")
                        : formatDateWithoutTime(dateRange.from) + " - " + formatDateWithoutTime(dateRange.to)
                }
                onClean={() => {
                    props.from("");
                    props.to("");
                    setDateRange({
                        from: "",
                        to: "",
                    });
                }}
            />
        </>
    );
};

export default connector(SidePanelDatePicker);
